/**
 * Shared Authentication Utilities
 * Centralized functions for getting current user info and company context
 * Used by all services to avoid code duplication
 */

export interface CurrentUserInfo {
  uid: string
  role: string
  companyId: string
}

interface StoredUserSnapshot {
  uid?: string
  role?: string
  companyId?: string
}

const getStoredUser = (): StoredUserSnapshot | null => {
  try {
    const userRaw = localStorage.getItem('user')
    if (!userRaw) return null
    return JSON.parse(userRaw) as StoredUserSnapshot
  } catch {
    return null
  }
}

/**
 * Get current company ID from stored user data
 * Used for multi-tenant data isolation
 */
export const getCurrentCompanyId = (): string | null => {
  const user = getStoredUser()
  return user?.companyId || null
}

/**
 * Get current user ID from stored user data
 */
export const getCurrentUserId = (): string | null => {
  const user = getStoredUser()
  return user?.uid || null
}

/**
 * Get a scoped localStorage key for the current user/company
 * - scope='company' uses companyId
 * - scope='user' uses uid
 * - scope='auto' prefers companyId, falls back to uid
 */
export const getScopedStorageKey = (
  baseKey: string,
  scope: 'company' | 'user' | 'auto' = 'auto'
): string => {
  const user = getStoredUser()
  const scopeId = scope === 'company'
    ? user?.companyId
    : scope === 'user'
      ? user?.uid
      : (user?.companyId || user?.uid)

  return scopeId ? `${baseKey}_${scopeId}` : `${baseKey}_anonymous`
}

/**
 * Get current user info including uid, role, and companyId
 * Used for role-based access control
 */
export const getCurrentUserInfo = (): CurrentUserInfo | null => {
  const user = getStoredUser()
  if (!user) return null
  return {
    uid: user.uid || null,
    role: user.role || 'staff',
    companyId: user.companyId || null
  }
}

/**
 * Check if user has admin or manager role
 */
export const isAdminOrManager = (): boolean => {
  const userInfo = getCurrentUserInfo()
  if (!userInfo) return false
  return (
    userInfo.role === 'admin' ||
    userInfo.role === 'org_admin' ||
    userInfo.role === 'super_admin' ||
    userInfo.role === 'manager'
  )
}

/**
 * Check if user has specific role
 */
export const hasRole = (role: string): boolean => {
  const userInfo = getCurrentUserInfo()
  if (!userInfo) return false
  return userInfo.role === role
}
